import { useNavigate } from 'react-router-dom';
import styles from './Legal.module.css';
import Navigation from '../Home/Navigation';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className={styles.container}>
      <Navigation />
      
      <main className={styles.mainContent}>
        <div className={styles.header}>
          <button 
            className={styles.backButton}
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            ← Back
          </button>
          <h1 className={styles.title}>Privacy Policy</h1>
          <p className={styles.lastUpdated}>Last Updated: November 24, 2025</p>
        </div>

        <div className={styles.content}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>1. Introduction</h2>
            <p className={styles.paragraph}>
              Welcome to Kingdom Studio ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. 
              This privacy policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered video 
              creation platform.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>2. Information We Collect</h2>
            
            <h3 className={styles.subsectionTitle}>2.1 Personal Information</h3>
            <p className={styles.paragraph}>
              We collect information that you provide directly to us, including:
            </p>
            <ul className={styles.list}>
              <li>Account information (name, email address, password)</li>
              <li>Payment information (processed securely through third-party payment processors)</li>
              <li>Profile information and preferences</li>
              <li>Communication data when you contact our support team</li>
            </ul>

            <h3 className={styles.subsectionTitle}>2.2 Content and Usage Data</h3>
            <ul className={styles.list}>
              <li>Videos, scripts, prompts, and other content you create or upload</li>
              <li>Project data and editing history</li>
              <li>Usage statistics and feature interactions</li>
              <li>Device information, IP address, and browser type</li>
            </ul>

            <h3 className={styles.subsectionTitle}>2.3 AI-Generated Content</h3>
            <p className={styles.paragraph}>
              We may process your inputs to our AI tools to generate scripts, prompts, audio, and video content. 
              This data is used solely to provide our services and improve our AI models.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>3. How We Use Your Information</h2>
            <p className={styles.paragraph}>
              We use your information to:
            </p>
            <ul className={styles.list}>
              <li>Provide, maintain, and improve our services</li>
              <li>Process your transactions and manage your account</li>
              <li>Generate AI-powered content based on your inputs</li>
              <li>Send you technical notices, updates, and support messages</li>
              <li>Respond to your comments, questions, and customer service requests</li>
              <li>Analyze usage patterns to enhance user experience</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>4. Data Sharing and Disclosure</h2>
            
            <h3 className={styles.subsectionTitle}>4.1 Third-Party Service Providers</h3>
            <p className={styles.paragraph}>
              We may share your information with third-party service providers who perform services on our behalf, including:
            </p>
            <ul className={styles.list}>
              <li>Cloud storage and hosting services</li>
              <li>Payment processors</li>
              <li>AI and machine learning service providers</li>
              <li>Analytics and monitoring services</li>
              <li>Customer support tools</li>
            </ul>

            <h3 className={styles.subsectionTitle}>4.2 Legal Requirements</h3>
            <p className={styles.paragraph}>
              We may disclose your information if required by law or in response to valid requests by public authorities.
            </p>

            <h3 className={styles.subsectionTitle}>4.3 Business Transfers</h3>
            <p className={styles.paragraph}>
              In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.
            </p>

            <h3 className={styles.subsectionTitle}>4.4 We Never Sell Your Data</h3>
            <p className={styles.paragraph}>
              We do not sell, rent, or trade your personal information to third parties for their marketing purposes.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>5. Data Security</h2>
            <p className={styles.paragraph}>
              We implement appropriate technical and organizational measures to protect your personal data against unauthorized 
              or unlawful processing, accidental loss, destruction, or damage. These measures include:
            </p>
            <ul className={styles.list}>
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Secure data centers and cloud infrastructure</li>
            </ul>
            <p className={styles.paragraph}>
              However, no method of transmission over the Internet or electronic storage is 100% secure. 
              While we strive to protect your data, we cannot guarantee absolute security.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>6. Data Retention</h2>
            <p className={styles.paragraph}>
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this privacy policy, 
              unless a longer retention period is required by law. Content you create may be retained as long as your account is active 
              or as needed to provide you services.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>7. Your Rights and Choices</h2>
            <p className={styles.paragraph}>
              Depending on your location, you may have the following rights:
            </p>
            <ul className={styles.list}>
              <li><strong>Access:</strong> Request access to your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Request a copy of your data in a structured format</li>
              <li><strong>Objection:</strong> Object to processing of your personal data</li>
              <li><strong>Withdrawal:</strong> Withdraw consent at any time (where processing is based on consent)</li>
            </ul>
            <p className={styles.paragraph}>
              To exercise these rights, please contact us at info@kingdomstudio.pro
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>8. Cookies and Tracking Technologies</h2>
            <p className={styles.paragraph}>
              We use cookies and similar tracking technologies to track activity on our platform and store certain information. 
              You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, 
              some features of our service may not function properly without cookies.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>9. Third-Party Links</h2>
            <p className={styles.paragraph}>
              Our platform may contain links to third-party websites. We are not responsible for the privacy practices 
              or content of these external sites. We encourage you to review their privacy policies.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>10. Children's Privacy</h2>
            <p className={styles.paragraph}>
              Our services are not directed to individuals under the age of 13. We do not knowingly collect personal information 
              from children. If you are a parent or guardian and believe your child has provided us with personal data, 
              please contact us immediately.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>11. International Data Transfers</h2>
            <p className={styles.paragraph}>
              Your information may be transferred to and processed in countries other than your country of residence. 
              These countries may have data protection laws different from your jurisdiction. We take appropriate safeguards 
              to ensure your data remains protected in accordance with this privacy policy.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>12. Changes to This Privacy Policy</h2>
            <p className={styles.paragraph}>
              We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy 
              on this page and updating the "Last Updated" date. You are advised to review this privacy policy periodically for changes.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>13. Contact Us</h2>
            <p className={styles.paragraph}>
              If you have any questions about this privacy policy or our data practices, please contact us:
            </p>
            <ul className={styles.list}>
              <li>Email: info@kingdomstudio.pro</li>
              <li>Website: kingdomstudio.pro</li>
            </ul>
          </section>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
